import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { TableDataResponse, TableFacetPayload, TablePayload } from '../models/utils-table';
import { BehaviorSubject } from 'rxjs';
import { TableType } from '../../../models/shipment';
import { ShareViewResponse } from '../models/share-view';
import { EmittedFacets } from '../../facet-filter/facet-filter.component';
import { DateRangeFacet, EmittedDateRangeFacet } from '../../date-range-facet-filter/date-range-facet-filter.component';
import { CustomFields } from '../../../pages/overview/models/shipment';

interface MappedFacetPayload extends TableFacetPayload {
  parameters: { value: string[] };
}

interface MappedDateRangeFacetPayload extends TableFacetPayload {
  parameters: { from: string; to: string };
}

interface UpdateTextFacetParams {
  currentFacets: TableFacetPayload[];
  facetIndex: number;
  facet: MappedFacetPayload;
  values: string[];
}

interface UpdateDateRangeFacetParams {
  currentFacets: TableFacetPayload[];
  facetIndex: number;
  facet: MappedDateRangeFacetPayload;
  newFacet: EmittedDateRangeFacet;
}

interface ContainerCustomFieldKeys {
  containerNo: string;
  masterNo: string;
  customerNo: string;
}

@Injectable({
  providedIn: 'root'
})
export class UtilsTableService {
  private pageConfigMap = new Map<number, BehaviorSubject<Partial<TablePayload>>>();
  private readonly defaultPageNumber = 1;
  private readonly defaultPageSize = 20;
  private readonly apiChunks: { [key: string]: string } = {
    Shipments: 'shipments',
    Containers: 'containers',
    Orders: 'purchaseOrders',
    OrderExceptions: 'purchaseOrderExceptions'
  };

  constructor(private http: HttpClient) {}

  getTableData(options: TablePayload, type: TableType = 'Shipments') {
    const apiUrlChunk = this.apiChunks[type];
    const body = {
      pageNumber: options?.pageNumber || this.defaultPageNumber,
      pageSize: options?.pageSize || this.defaultPageSize,
      search: options?.search || null,
      filters: options?.filters || [],
      sort: options?.sort || null,
      tabId: options.tabId
    };
    return this.http.post<TableDataResponse>(`${environment.apiUrl}/${apiUrlChunk}/search`, body);
  }

  getArchivedTableData(options: Partial<TablePayload>, tabType: TableType) {
    const { pageNumber = this.defaultPageNumber, pageSize = this.defaultPageSize, search } = options;
    const apiUrlChunk = this.apiChunks[tabType];
    return this.http.post<TableDataResponse>(`${environment.apiUrl}/archive/${apiUrlChunk}/search`, {
      pageNumber,
      pageSize,
      searchString: search?.value
    });
  }

  getPageConfigFor(tabId: number) {
    return this.getOrCreatePageConfigFor(tabId).asObservable();
  }

  updatePageConfig(tabId: number, options: Partial<TablePayload>) {
    const config = this.getOrCreatePageConfigFor(tabId);
    config.next({ ...config.value, ...options });
  }

  resetPageConfig(tabId: number) {
    const config = this.getOrCreatePageConfigFor(tabId);
    config.next({ tabId, filters: [], sort: null, search: null });
  }

  resetAllFilters() {
    this.pageConfigMap.clear();
  }

  setTextFacets(tabId: number, { columnKey, filterKey, values }: EmittedFacets) {
    const config = this.getOrCreatePageConfigFor(tabId);
    const currentFacets = config.value.filters || [];
    const facetIndex = currentFacets.findIndex(el => el.columnKey === columnKey);
    const facet = currentFacets.find(el => el.columnKey === columnKey);
    if (facet) {
      const mappedFacet = facet as MappedFacetPayload;
      this.updateTextFacet({ currentFacets, facetIndex, facet: mappedFacet, values: values });
    } else {
      this.addNewFacet(currentFacets, { columnKey, filterKey, values });
    }
    config.next({ ...config.value, filters: currentFacets });
  }

  setDateRangeFacet(tabId: number, newFacet: EmittedDateRangeFacet) {
    const config = this.getOrCreatePageConfigFor(tabId);
    const currentFacets = config.value.filters || [];
    const facetIndex = currentFacets.findIndex(el => el.columnKey === newFacet.columnKey);
    const facet = currentFacets.find(el => el.columnKey === newFacet.columnKey);
    if (facet) {
      const mappedFacet = facet as MappedDateRangeFacetPayload;
      this.updateDateRangeFacet({ currentFacets, facetIndex, facet: mappedFacet, newFacet });
    } else {
      this.addNewDateRangeFacet(currentFacets, newFacet);
    }
    config.next({ ...config.value, filters: currentFacets });
  }

  updateShipmentCustomFields(shipmentNumbers: string[], data: CustomFields) {
    return this.http.put(`${environment.apiUrl}/shipments/customFields`, { shipmentNumbers, data });
  }

  updateContainerCustomFields(keys: ContainerCustomFieldKeys[], data: CustomFields) {
    return this.http.put(`${environment.apiUrl}/containers/customFields`, { keys, data });
  }

  validateView(userEmail: string, tabId: number) {
    return this.http.post<ShareViewResponse>(`${environment.apiUrl}/tabs/exists`, { userEmail, tabId });
  }

  shareView(targetUserEmail: string, tabId: number) {
    return this.http.post<unknown>(`${environment.apiUrl}/tabs/${tabId}/copy`, { targetUserEmail });
  }

  private getOrCreatePageConfigFor(tabId: number) {
    if (!this.pageConfigMap.has(tabId)) {
      this.pageConfigMap.set(tabId, new BehaviorSubject<Partial<TablePayload>>({ tabId }));
    }
    return this.pageConfigMap.get(tabId)!;
  }

  private updateTextFacet({ currentFacets, facetIndex, facet, values }: UpdateTextFacetParams) {
    if (this.areFacetsValuesEmpty(values)) {
      currentFacets.splice(facetIndex, 1);
    } else {
      facet.parameters.value = values;
    }
  }

  private addNewFacet(currentFacets: TableFacetPayload[], { columnKey, filterKey, values }: EmittedFacets) {
    if (!this.areFacetsValuesEmpty(values)) {
      const parameters = { value: values };
      currentFacets.push({ columnKey, filterKey, parameters });
    }
  }

  private addNewDateRangeFacet(
    currentFacets: TableFacetPayload[],
    { columnKey, filterKey, values }: EmittedDateRangeFacet
  ) {
    if (!this.areDateRangeFacetsValuesEmpty(values)) {
      currentFacets.push({ columnKey, filterKey, parameters: values as MappedDateRangeFacetPayload['parameters'] });
    } else if (filterKey === 'hasNoValue') {
      currentFacets.push({ columnKey, filterKey, parameters: { value: [] } });
    }
  }

  private updateDateRangeFacet({ currentFacets, facetIndex, facet, newFacet }: UpdateDateRangeFacetParams) {
    if (newFacet.filterKey !== facet.filterKey) {
      currentFacets.splice(facetIndex, 1);
      this.addNewDateRangeFacet(currentFacets, newFacet);
    } else if (this.areDateRangeFacetsValuesEmpty(newFacet.values)) {
      currentFacets.splice(facetIndex, 1);
    } else {
      facet.parameters = newFacet.values as MappedDateRangeFacetPayload['parameters'];
    }
  }

  private areFacetsValuesEmpty(values: string[]) {
    return !values || (Array.isArray(values) && !values.length);
  }

  private areDateRangeFacetsValuesEmpty(values?: DateRangeFacet) {
    return !values || (!values.from && !values.to);
  }
}
